/** Membership management: Apple status, web billing, or restore. */
import { HelpScoutBeacon } from '@/components/HelpScoutBeacon';
import {
  SettingsPanel,
  SettingsPanelBody,
} from '@/components/settings/SettingsPanel';
import { PlanScheduleCard } from '@/components/subscription/PostPurchaseSuccess';
import Container from '@/components/ui/Container';
import { BodyText, CaptionText, PageTitle } from '@/components/ui/Typography';
import { useAuth } from '@/lib/AuthContext';
import { BRAND_COLORS as C } from '@/lib/brandColors';
import { URLS } from '@/lib/constants';
import { readMembershipSource } from '@/lib/membershipSource';
import {
  resolvePendingPlanChange,
  type PendingPlanChange,
} from '@/lib/pendingPlanChange';
import { isMemberTier } from '@/lib/membershipTier';
import {
  addCustomerInfoListener,
  getManagementUrl,
  grantAllPlanFamily,
  hasActiveEntitlement,
  isPurchasesAvailable,
  refreshCustomerInfo,
  restorePurchases,
} from '@/lib/purchases/RevenueCat';
import {
  describeAppleSubscription,
  STATUS_TONE_COLORS,
  type StatusTone,
} from '@/lib/purchases/subscriptionStatus';
import { CONTENT_MAX_WIDTH, useIsMobile } from '@/lib/useDeviceType';
import { useFocusEffect, useRouter } from 'expo-router';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ActivityIndicator, AppState, Linking, Platform, ScrollView } from 'react-native';
import type { CustomerInfo } from 'react-native-purchases';
import { Button, XStack, YStack } from 'tamagui';

const RESUME_REFRESH_DELAYS_MS = [0, 1500, 3000];

function tierLabel(tier: string): string {
  if (tier === 'pro') return 'Open Studio Pro';
  if (tier === 'regular') return 'Open Studio';
  return 'No active membership';
}

export default function MembershipScreen() {
  const router = useRouter();
  const isMobile = useIsMobile();
  const { membershipTier, refreshMembership, preferenceMetadata, user } = useAuth();
  const [info, setInfo] = useState<CustomerInfo | null>(null);
  const [pendingChange, setPendingChange] = useState<PendingPlanChange | null>(null);
  const [loading, setLoading] = useState(true);
  const [restoring, setRestoring] = useState(false);
  const [restoreError, setRestoreError] = useState<string | null>(null);
  const hasLoadedRef = useRef(false);
  const mountedRef = useRef(true);
  const iapAvailable = isPurchasesAvailable();

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const load = useCallback(async () => {
    const [customer] = await Promise.all([
      refreshCustomerInfo(),
      refreshMembership(),
    ]);
    if (!mountedRef.current) return;
    setInfo(customer);
    const pending = await resolvePendingPlanChange(user?.id, customer);
    if (!mountedRef.current) return;
    setPendingChange(pending);
    hasLoadedRef.current = true;
    setLoading(false);
  }, [refreshMembership, user?.id]);

  useFocusEffect(
    useCallback(() => {
      if (!hasLoadedRef.current) setLoading(true);
      void load();
    }, [load]),
  );

  // Stay on this screen while managing in Apple Settings, so refresh on resume.
  useEffect(() => {
    const subscription = AppState.addEventListener('change', (state) => {
      if (state !== 'active') return;
      void (async () => {
        for (const delayMs of RESUME_REFRESH_DELAYS_MS) {
          if (delayMs) await new Promise((r) => setTimeout(r, delayMs));
          if (!mountedRef.current) return;
          await load();
        }
      })();
    });
    return () => subscription.remove();
  }, [load]);

  useEffect(
    () =>
      addCustomerInfoListener((customer) => {
        if (!mountedRef.current) return;
        setInfo(customer);
        void resolvePendingPlanChange(user?.id, customer).then((pending) => {
          if (mountedRef.current) setPendingChange(pending);
        });
      }),
    [user?.id],
  );

  const onRestore = useCallback(async () => {
    setRestoring(true);
    setRestoreError(null);
    try {
      const restored = await restorePurchases();
      await refreshMembership();
      const fresh = (await refreshCustomerInfo()) ?? restored;
      setInfo(fresh);
      if (!hasActiveEntitlement(fresh)) {
        setRestoreError(
          'No Apple subscription found for the Apple ID signed in on this device.',
        );
      }
    } catch {
      setRestoreError('Could not restore purchases. Please try again.');
    } finally {
      setRestoring(false);
    }
  }, [refreshMembership]);

  // Web has no in-app billing: the membership lives on the WordPress account
  // page (same URL as the "Manage" button), so send browsers straight there.
  useEffect(() => {
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      window.location.replace(URLS.MY_ACCOUNT);
    }
  }, []);

  const sub = describeAppleSubscription(info);
  // Apple-billed normal members can move to Pro in-app: same subscription
  // group, so Apple prorates the switch (same path The Hang's /go-pro CTA
  // takes). Pro members and web-billed members never see this.
  const canUpgradeToPro = Boolean(sub && grantAllPlanFamily(sub.productId) === 'normal');
  const isMember = isMemberTier(membershipTier);
  const source = readMembershipSource(preferenceMetadata);
  const sectionGap = isMobile ? '$3' : '$4';
  const manageWithApple = () => Linking.openURL(getManagementUrl(info));

  return (
    <YStack f={1} bg={C.pageBg}>
      <ScrollView
        contentContainerStyle={{ paddingBottom: isMobile ? 40 : 56 }}
        showsVerticalScrollIndicator={false}
      >
        <Container
          maxWidth={CONTENT_MAX_WIDTH}
          centered
          py={isMobile ? '$4' : '$6'}
        >
          <PageTitle color={C.text} mb={sectionGap} ta="left">
            Membership
          </PageTitle>

          {loading || Platform.OS === 'web' ? (
            <YStack ai="center" jc="center" py="$10" w="100%">
              <ActivityIndicator color={C.accent} size="large" />
            </YStack>
          ) : sub ? (
            <SettingsPanel title={sub.planName} subtitle={sub.statusLine}>
              <SettingsPanelBody>
                <Badge
                  text={pendingChange ? 'Plan change scheduled' : sub.badge.label}
                  tone={pendingChange ? 'warning' : sub.badge.tone}
                />
                {pendingChange ? (
                  <PlanScheduleCard align="left" {...pendingChange} />
                ) : null}
                <CaptionText color={C.muted} ta="left">
                  Purchased through Apple
                </CaptionText>

                <YStack gap="$3" mt="$2" ai="flex-start">
                  {sub.state === 'billing_issue' ? (
                    <PrimaryButton onPress={manageWithApple}>
                      Update payment with Apple
                    </PrimaryButton>
                  ) : sub.state === 'lifetime' ? (
                    <SecondaryButton onPress={manageWithApple}>
                      View Apple purchases
                    </SecondaryButton>
                  ) : sub.willRenew ? (
                    <>
                      {canUpgradeToPro ? (
                        <PrimaryButton onPress={() => router.push('/upgrade?mode=pro')}>
                          Upgrade to Open Studio Pro
                        </PrimaryButton>
                      ) : null}
                      {canUpgradeToPro ? (
                        <SecondaryButton onPress={() => router.push('/upgrade?mode=change')}>
                          Change plan
                        </SecondaryButton>
                      ) : (
                        <PrimaryButton onPress={() => router.push('/upgrade?mode=change')}>
                          Change plan
                        </PrimaryButton>
                      )}
                      <SecondaryButton onPress={manageWithApple}>
                        Manage or cancel with Apple
                      </SecondaryButton>
                    </>
                  ) : (
                    <PrimaryButton onPress={manageWithApple}>
                      Turn renewal back on
                    </PrimaryButton>
                  )}
                </YStack>

                <CaptionText color={C.muted} ta="left" mt="$2">
                  {sub.helpText}
                </CaptionText>
              </SettingsPanelBody>
            </SettingsPanel>
          ) : isMember && source === 'apple' ? (
            <SettingsPanel title={tierLabel(membershipTier)}>
              <SettingsPanelBody>
                <Badge text="Active" tone="success" />
                <BodyText color={C.muted} ta="left">
                  This membership is billed through Apple, but the Apple ID
                  signed in on this device is not the one that bought it. Sign in
                  with that Apple ID, or restore below.
                </BodyText>
                <YStack gap="$3" mt="$3" ai="flex-start">
                  <PrimaryButton onPress={onRestore} disabled={restoring}>
                    {restoring ? 'Restoring…' : 'Restore purchases'}
                  </PrimaryButton>
                  <SecondaryButton onPress={manageWithApple}>
                    Manage with Apple
                  </SecondaryButton>
                </YStack>
                {!!restoreError && (
                  <CaptionText color={C.error} ta="left" mt="$2">
                    {restoreError}
                  </CaptionText>
                )}
              </SettingsPanelBody>
            </SettingsPanel>
          ) : isMember ? (
            <SettingsPanel title={tierLabel(membershipTier)}>
              <SettingsPanelBody>
                <Badge text="Active" tone="success" />
                <BodyText color={C.muted} ta="left">
                  Your membership is billed on the web. Update your payment
                  details, download invoices, or change your plan there.
                </BodyText>
                <YStack gap="$3" mt="$2" ai="flex-start">
                  <SecondaryButton
                    onPress={() => Linking.openURL(URLS.MY_ACCOUNT)}
                  >
                    Manage
                  </SecondaryButton>
                  {iapAvailable && (
                    <BodyText color={C.muted} ta="left">
                      Bought on another Apple ID?{' '}
                      <BodyText
                        color={C.accent}
                        textDecorationLine="underline"
                        onPress={restoring ? undefined : onRestore}
                        opacity={restoring ? 0.6 : 1}
                        hitSlop={12}
                        accessibilityRole="link"
                      >
                        {restoring ? 'Restoring…' : 'Restore purchases'}
                      </BodyText>
                    </BodyText>
                  )}
                </YStack>
                {!!restoreError && (
                  <CaptionText color={C.error} ta="left" mt="$2">
                    {restoreError}
                  </CaptionText>
                )}
              </SettingsPanelBody>
            </SettingsPanel>
          ) : (
            <SettingsPanel title="No active membership">
              <SettingsPanelBody>
                <BodyText color={C.muted} ta="left">
                  Unlock every course, live sessions, and the full community.
                </BodyText>
                <YStack gap="$3" mt="$2" ai="flex-start">
                  <PrimaryButton onPress={() => router.push('/upgrade')}>
                    View plans
                  </PrimaryButton>
                  {iapAvailable && (
                    <SecondaryButton onPress={onRestore} disabled={restoring}>
                      {restoring ? 'Restoring…' : 'Restore purchases'}
                    </SecondaryButton>
                  )}
                </YStack>
                {!!restoreError && (
                  <CaptionText color={C.error} ta="left" mt="$2">
                    {restoreError}
                  </CaptionText>
                )}
              </SettingsPanelBody>
            </SettingsPanel>
          )}
        </Container>
      </ScrollView>
      <HelpScoutBeacon />
    </YStack>
  );
}

function Badge({ text, tone }: { text: string; tone: StatusTone }) {
  const color = STATUS_TONE_COLORS[tone];
  return (
    <XStack
      alignSelf="flex-start"
      bg={C.cardBg}
      borderWidth={1}
      borderColor={color}
      px="$2.5"
      py="$1"
      br="$10"
    >
      <CaptionText color={color} fontWeight="700">
        {text}
      </CaptionText>
    </XStack>
  );
}

function PrimaryButton({
  children,
  onPress,
  disabled,
}: {
  children: React.ReactNode;
  onPress: () => void;
  disabled?: boolean;
}) {
  return (
    <Button
      onPress={onPress}
      disabled={disabled}
      opacity={disabled ? 0.6 : 1}
      bg={C.accent}
      color={C.cardBg}
      fontWeight="700"
      br="$4"
      h={44}
      px="$5"
      alignSelf="flex-start"
      pressStyle={{ opacity: 0.85 }}
      hoverStyle={{ opacity: 0.9 }}
    >
      {children}
    </Button>
  );
}

function SecondaryButton({
  children,
  onPress,
  disabled,
}: {
  children: React.ReactNode;
  onPress: () => void;
  disabled?: boolean;
}) {
  return (
    <Button
      onPress={onPress}
      disabled={disabled}
      opacity={disabled ? 0.6 : 1}
      bg={C.cardBg}
      color={C.accent}
      fontWeight="700"
      br="$4"
      h={44}
      px="$5"
      alignSelf="flex-start"
      borderWidth={1}
      borderColor={C.accent}
      pressStyle={{ opacity: 0.85 }}
    >
      {children}
    </Button>
  );
}
